import React, { memo } from 'react';
import { Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { formatDate } from 'utility/functions';
import { HHMM, MMM_DD_YYYY } from 'utility/constants';

export type DateCellProps = {
  date?: string | null | undefined;
};

/**
 * DateCell - Displays formatted date and time
 *
 * Re-used in collections and other asset list components.
 * Shows date and time in separate lines with site timezone conversion.
 */
export const DateCell = memo(({ date }: DateCellProps) => {
  const { sessionConfig } = useCosmos();
  const siteTimeZone = sessionConfig?.time_zone;
  const theme = useTheme();

  if (!date) {
    return null;
  }

  const dateFormatted = formatDate({
    date: date,
    shiftTz: siteTimeZone,
    format: MMM_DD_YYYY,
  });

  const timeFormatted = formatDate({
    date: date,
    shiftTz: siteTimeZone,
    format: HHMM,
  });

  return (
    <Stack
      direction="column"
      title={`${dateFormatted} at ${timeFormatted}`}
    >
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: 1,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          fontWeight: theme.typography.fontWeightRegular,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {dateFormatted}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: 1,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          fontWeight: theme.typography.fontWeightRegular,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {timeFormatted}
      </Typography>
    </Stack>
  );
});
DateCell.displayName = 'DateCell';
